/**
 * Which boxes a hierarchy draws: a lineage to focus on, and what is folded away.
 *
 * Issue #93. A program's requirements drawn all at once are unreadable by
 * construction - a few hundred boxes in five bands - so the diagrams draw a
 * SUBSET, chosen two ways that compose:
 *
 * - `lineage` keeps one or more seeds and everything they trace up to and down
 *   from. Nothing sideways: a sibling of the seed is not in its lineage.
 * - `fold` hides what hangs under a box, by band ("show down to L2") and box by
 *   box, the way a tree view collapses.
 *
 * Generic over `DiagramNode` and its band, like `layout`, so the parts
 * hierarchy folds by depth with the same code - and so a later one-element
 * focus diagram (a state definition's, say) can reuse it. Pure, and asserted on
 * keys in `focus.test.ts`: a fold that hides one box too many draws a tidy,
 * plausible diagram that is missing a requirement.
 */

import type { DiagramNode } from './layout'

/**
 * Above this many requirements the hierarchy does not open on everything.
 *
 * 100 because that is roughly where the whole model, even wrapped eight to a
 * row, fits a pane only at about a third of natural size - boxes too small to
 * read without zooming, which is the problem issue #93 reported. The Example
 * program has eleven, so it is unaffected.
 */
export const FOLD_BY_DEFAULT_ABOVE = 100

/** Every key reached from `start` by following `step`, not counting the start itself. */
function reach(start: Iterable<string>, step: (key: string) => readonly string[]): Set<string> {
  const found = new Set<string>()
  const queue = [...start]
  while (queue.length) {
    const key = queue.pop() as string
    for (const next of step(key)) {
      if (found.has(next)) continue
      found.add(next)
      queue.push(next)
    }
  }
  return found
}

/** Child keys by parent key, counting only parents that are present. */
function childrenOf(nodes: readonly DiagramNode[]): Map<string, string[]> {
  const present = new Set(nodes.map((node) => node.key))
  const children = new Map<string, string[]>()
  for (const node of nodes) {
    for (const parent of node.parentKeys) {
      if (!present.has(parent)) continue
      const list = children.get(parent)
      if (list) list.push(node.key)
      else children.set(parent, [node.key])
    }
  }
  return children
}

/**
 * The seeds, everything they derive from, and everything derived from them.
 *
 * Up through every parent and down through every child, each walked to its
 * end: ancestors to the roots, descendants to the leaves. A cycle in the model
 * (which it permits, and the findings report) ends the walk rather than looping.
 * A seed that is not among the nodes is ignored. The answer keeps the nodes'
 * own order, so the layout orders the subset exactly as it orders the whole.
 *
 * @param[in] nodes - DiagramNode[] : the whole diagram
 * @param[in] seeds - Iterable<string> : keys to focus on
 * @return DiagramNode[]
 */
export function lineage(nodes: readonly DiagramNode[], seeds: Iterable<string>): DiagramNode[] {
  const byKey = new Map(nodes.map((node) => [node.key, node]))
  const start = [...seeds].filter((key) => byKey.has(key))
  if (start.length === 0) return []
  const children = childrenOf(nodes)

  const keep = new Set(start)
  for (const key of reach(start, (at) => byKey.get(at)?.parentKeys.filter((p) => byKey.has(p)) ?? [])) {
    keep.add(key)
  }
  for (const key of reach(start, (at) => children.get(at) ?? [])) keep.add(key)
  return nodes.filter((node) => keep.has(node.key))
}

/**
 * How a hierarchy is folded.
 *
 * `cutoff` is the deepest band drawn by default, or null for every band; boxes
 * in that band show what hangs under them as a count, closed. `open` is a box's
 * own setting, which wins over the cutoff: true opens it even below the cutoff,
 * false closes it even above.
 */
export interface Folding {
  cutoff: number | null
  open: ReadonlyMap<string, boolean>
}

export const UNFOLDED: Folding = { cutoff: null, open: new Map() }

/** What `fold` decided about one box that has something under it. */
export interface Fold {
  /** True when every one of its children is drawn. */
  open: boolean
  /**
   * How many boxes are hidden under it: its hidden children and everything
   * hidden under those. 0 when all of its children are drawn.
   */
  hidden: number
}

export interface Folded {
  /** The boxes to draw, in the input's order. */
  nodes: DiagramNode[]
  /** One entry per DRAWN box that has children; a leaf has none. */
  folds: Map<string, Fold>
}

/**
 * Hide what folded boxes hold, and say how much each one is holding.
 *
 * A box is drawn if it is a root, or if any of its parents is drawn, is open,
 * and lets it through. An open parent lets a child through when the child is
 * at or above the cutoff - or always, when the parent was opened by hand. That
 * second rule is what makes "show down to L2" mean it: an L4 that names an L1
 * source directly stays hidden until somebody opens that L1, rather than
 * appearing three levels under a cutoff it skipped.
 *
 * Because the hierarchy is a DAG, a box with several parents is drawn if ANY of
 * them lets it through. Folding one source does not hide a requirement another
 * drawn source still holds.
 *
 * A root is a box none of whose parents is present. A box reachable from no
 * root at all - only possible inside a cycle - is treated as a root too, so a
 * cycle is drawn rather than silently folded out of sight.
 *
 * @param[in] nodes - DiagramNode[] : the boxes before folding
 * @param[in] folding - Folding : the cutoff and the per-box settings
 * @return Folded
 */
export function fold(nodes: readonly DiagramNode[], folding: Folding): Folded {
  const byKey = new Map(nodes.map((node) => [node.key, node]))
  const children = childrenOf(nodes)
  const { cutoff, open } = folding

  const isOpen = (node: DiagramNode) => open.get(node.key) ?? (cutoff === null || node.band < cutoff)
  const letsThrough = (parent: DiagramNode, child: DiagramNode) =>
    isOpen(parent) && (open.get(parent.key) === true || cutoff === null || child.band <= cutoff)

  const roots = nodes.filter((node) => !node.parentKeys.some((key) => byKey.has(key)))
  const reachable = new Set(roots.map((node) => node.key))
  for (const key of reach(reachable, (at) => children.get(at) ?? [])) reachable.add(key)
  // Cycle members nothing outside the cycle leads to. One per cycle is enough
  // to draw it, so each becomes a root only if an earlier one did not reach it.
  for (const node of nodes) {
    if (reachable.has(node.key)) continue
    roots.push(node)
    reachable.add(node.key)
    for (const key of reach([node.key], (at) => children.get(at) ?? [])) reachable.add(key)
  }

  const drawn = new Set<string>()
  const queue = roots.map((node) => node.key)
  for (const key of queue) drawn.add(key)
  while (queue.length) {
    const parent = byKey.get(queue.pop() as string) as DiagramNode
    for (const childKey of children.get(parent.key) ?? []) {
      if (drawn.has(childKey)) continue
      if (!letsThrough(parent, byKey.get(childKey) as DiagramNode)) continue
      drawn.add(childKey)
      queue.push(childKey)
    }
  }

  const folds = new Map<string, Fold>()
  for (const key of drawn) {
    const own = children.get(key)
    if (!own?.length) continue
    const hiddenChildren = own.filter((child) => !drawn.has(child))
    const below = reach(hiddenChildren, (at) => children.get(at) ?? [])
    for (const child of hiddenChildren) below.add(child)
    let hidden = 0
    for (const entry of below) if (!drawn.has(entry)) hidden += 1
    folds.set(key, { open: hiddenChildren.length === 0, hidden })
  }

  return { nodes: nodes.filter((node) => drawn.has(node.key)), folds }
}

/**
 * The folding after somebody clicks a box's fold toggle.
 *
 * A box hiding anything opens, showing every child; a box showing all of its
 * children closes. Setting the box's own entry, so the cutoff stays as it was
 * for everything else.
 *
 * @param[in] folding - Folding : before
 * @param[in] key - string : the box clicked
 * @param[in] state - Fold : what `fold` said about that box
 * @return Folding
 */
export function toggle(folding: Folding, key: string, state: Fold): Folding {
  const open = new Map(folding.open)
  open.set(key, state.hidden > 0)
  return { cutoff: folding.cutoff, open }
}

/**
 * Where a large diagram is folded when it first opens: down to its second band.
 *
 * For requirements that is L1, the mission's first breakdown, each box showing
 * how much hangs under it. Null - nothing folded - at or under the threshold,
 * and for a diagram of one band, where there is nothing to fold.
 *
 * @param[in] nodes - DiagramNode[] : the boxes about to be drawn
 * @param[in] threshold - number : the count at or under which nothing folds
 * @return number | null
 */
export function defaultCutoff(
  nodes: readonly DiagramNode[],
  threshold = FOLD_BY_DEFAULT_ABOVE,
): number | null {
  if (nodes.length <= threshold) return null
  const bands = [...new Set(nodes.map((node) => node.band))].sort((a, b) => a - b)
  return bands.length > 1 ? bands[1] : null
}
